import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import ReactDOM from "react-dom";
import { IconX } from "@tabler/icons-react";
import { cn } from "@/shared/lib/utils";
import { useToastStore } from "./toastStore";
import styles from "./Toast.module.scss";
const TONE_CLASS = {
    info: styles.toneInfo,
    success: styles.toneSuccess,
    error: styles.toneError,
    warning: styles.toneWarning,
};
const ICONS = {
    info: (_jsxs("svg", { viewBox: "0 0 18 18", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "9", cy: "9", r: "7.5" }), _jsx("path", { d: "M9 8v4M9 5.5h.01" })] })),
    success: (_jsxs("svg", { viewBox: "0 0 18 18", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "9", cy: "9", r: "7.5" }), _jsx("path", { d: "M5.5 9.2l2.5 2.5 5-5" })] })),
    error: (_jsxs("svg", { viewBox: "0 0 18 18", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "9", cy: "9", r: "7.5" }), _jsx("path", { d: "M6 6l6 6M12 6l-6 6" })] })),
    warning: (_jsxs("svg", { viewBox: "0 0 18 18", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M9 2.5l7 12.5H2L9 2.5z" }), _jsx("path", { d: "M9 7v4M9 13h.01" })] })),
};
const ToastRow = ({ toast }) => {
    const dismiss = useToastStore((s) => s.dismiss);
    const [leaving, setLeaving] = React.useState(false);
    const remove = React.useCallback((runOnClose) => {
        // Крестик (runOnClose) выполняет действие тоста (напр. пометить
        // уведомление прочитанным); авто-дисмисс просто убирает тост.
        if (runOnClose)
            toast.onClose?.();
        // Play the exit animation, then remove from the store.
        setLeaving(true);
        window.setTimeout(() => dismiss(toast.id), 180);
    }, [dismiss, toast]);
    const close = React.useCallback(() => remove(true), [remove]);
    // «Перейти»: выполняет действие тоста (навигация + пометка прочитанным внутри
    // самого действия), затем убирает тост без повторного onClose.
    const runAction = React.useCallback(() => {
        toast.action?.onAction();
        setLeaving(true);
        window.setTimeout(() => dismiss(toast.id), 180);
    }, [dismiss, toast]);
    React.useEffect(() => {
        if (toast.duration <= 0)
            return;
        const id = window.setTimeout(() => remove(false), toast.duration);
        return () => window.clearTimeout(id);
    }, [toast.duration, remove]);
    return (_jsxs("div", { className: cn(styles.toast, TONE_CLASS[toast.tone], leaving && styles.leaving), role: toast.tone === "error" ? "alert" : "status", children: [_jsx("span", { className: styles.icon, "aria-hidden": "true", children: ICONS[toast.tone] }), _jsxs("div", { className: styles.body, children: [toast.title && _jsx("div", { className: styles.title, children: toast.title }), toast.message && _jsx("div", { className: styles.message, children: toast.message })] }), toast.action && (_jsx("button", { type: "button", className: styles.action, onClick: runAction, children: toast.action.label })), _jsx("button", { type: "button", className: styles.close, onClick: close, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", children: _jsx(IconX, { size: 16, stroke: 2, "aria-hidden": "true" }) })] }));
};
/**
 * Renders the live toast stack. Mount exactly once near the app root —
 * `toast.*` / `notifications.show` push into the shared store and this
 * component reacts.
 */
export const Toaster = () => {
    const toasts = useToastStore((s) => s.toasts);
    if (typeof document === "undefined" || toasts.length === 0)
        return null;
    return ReactDOM.createPortal(_jsx("div", { className: styles.viewport, "aria-live": "polite", children: toasts.map((t) => (_jsx(ToastRow, { toast: t }, t.id))) }), document.body);
};
